import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { lazy, Suspense, useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { isStaleChunkError, recoverFromStaleChunk } from './lib/stale-chunk';
import Layout from './components/layout/Layout';
import ScrollToTop from './components/ScrollToTop';
import { usePageTracking } from './hooks/usePageTracking';
import CookieConsent from './components/CookieConsent';
import { ToastProvider } from './components/Toast';
import ScrollIndicator from './components/ScrollIndicator';
import LoadingScreen from './components/LoadingScreen';

// Lazy-load non-critical UI overlays (not needed on first paint)
const SearchModal = lazy(() => import('./components/SearchModal'));
const ConsultationBanner = lazy(() => import('./components/ConsultationBanner'));
const ExitIntent = lazy(() => import('./components/ExitIntent'));
import { A11yAuditProvider } from './components/A11yAuditProvider';

const HomePage = lazy(() => import('./pages/HomePage'));
const ServicesPage = lazy(() => import('./pages/ServicesPage'));
const PortfolioPage = lazy(() => import('./pages/PortfolioPage'));
const CaseStudiesPage = lazy(() => import('./pages/portfolio/CaseStudiesPage'));
const CaseStudyPage = lazy(() => import('./pages/portfolio/CaseStudyPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const BlogPage = lazy(() => import('./pages/blog/BlogPage'));
const BlogPostPage = lazy(() => import('./pages/blog/BlogPostPage'));
const PricingPage = lazy(() => import('./pages/PricingPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const CareersPage = lazy(() => import('./pages/CareersPage'));
const TestimonialsPage = lazy(() => import('./pages/TestimonialsPage'));
const FAQPage = lazy(() => import('./pages/FAQPage'));
const EstimatorPage = lazy(() => import('./pages/EstimatorPage'));
const ResetPasswordPage = lazy(() => import('./pages/portal/ResetPasswordPage'));
const AccountDeletePage = lazy(() => import('./pages/AccountDeletePage'));
const AccountDeleteConfirmPage = lazy(() => import('./pages/AccountDeleteConfirmPage'));

// Admin pages
const AdminLoginPage = lazy(() => import('./pages/admin/AdminLoginPage'));
const AdminLayout = lazy(() => import('./pages/admin/AdminLayout'));
const AdminDashboardPage = lazy(() => import('./pages/admin/AdminDashboardPage'));
const AdminContactsPage = lazy(() => import('./pages/admin/AdminContactsPage'));
const AdminProjectsPage = lazy(() => import('./pages/admin/AdminProjectsPage'));
const AdminSubscribersPage = lazy(() => import('./pages/admin/AdminSubscribersPage'));
const AdminAnalyticsPage = lazy(() => import('./pages/admin/AdminAnalyticsPage'));
const AdminPortalPage = lazy(() => import('./pages/admin/AdminPortalPage'));

const queryClient = new QueryClient();

function PageLoader() {
  return (
    <div className="flex min-h-[50vh] items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
    </div>
  );
}

function PageTracker() {
  usePageTracking();
  return null;
}

function ErrorFallback({ error }: { error?: unknown }) {
  // Belt-and-suspenders for ICODE-208: if a lazy import rejected with a
  // stale-chunk error and slipped past the vite:preloadError handler, attempt
  // the same guarded one-shot reload here instead of showing the fallback.
  const message = error instanceof Error ? error.message : String(error ?? '');
  const isStale = isStaleChunkError(message);

  useEffect(() => {
    if (isStale) recoverFromStaleChunk();
  }, [isStale]);

  if (isStale) return <></>;

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <div className="max-w-md text-center">
        <h1 className="mb-4 text-2xl font-bold text-foreground">Something went wrong</h1>
        <p className="mb-6 text-muted-foreground">
          We've been notified and are working on a fix. Please try refreshing the page.
        </p>
        <button
          onClick={() => window.location.reload()}
          className="rounded-md bg-primary px-4 py-2 text-primary-foreground hover:bg-primary/90"
        >
          Refresh Page
        </button>
      </div>
    </div>
  );
}

function App() {
  return (
    <Sentry.ErrorBoundary fallback={ErrorFallback} showDialog>
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
      <LoadingScreen />
      <BrowserRouter>
        <A11yAuditProvider>
        <PageTracker />
        <ScrollToTop />
        <Suspense fallback={null}><SearchModal /></Suspense>
        <ScrollIndicator />
        <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Public site */}
          <Route element={<Layout />}>
            <Route path="/" element={<HomePage />} />
            <Route path="/services" element={<ServicesPage />} />
            <Route path="/portfolio" element={<PortfolioPage />} />
            <Route path="/case-studies" element={<CaseStudiesPage />} />
            <Route path="/portfolio/:slug" element={<CaseStudyPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/:slug" element={<BlogPostPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/careers" element={<CareersPage />} />
            <Route path="/testimonials" element={<TestimonialsPage />} />
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/estimator" element={<EstimatorPage />} />
            <Route path="/account/delete" element={<AccountDeletePage />} />
            <Route path="/account/delete/confirm" element={<AccountDeleteConfirmPage />} />
          </Route>

          {/* Client portal (standalone, noindex) */}
          <Route path="/portal/reset-password" element={<ResetPasswordPage />} />

          {/* Admin */}
          <Route path="/admin" element={<AdminLoginPage />} />
          <Route element={<AdminLayout />}>
            <Route path="/admin/dashboard" element={<AdminDashboardPage />} />
            <Route path="/admin/contacts" element={<AdminContactsPage />} />
            <Route path="/admin/projects" element={<AdminProjectsPage />} />
            <Route path="/admin/subscribers" element={<AdminSubscribersPage />} />
            <Route path="/admin/analytics" element={<AdminAnalyticsPage />} />
            <Route path="/admin/portal" element={<AdminPortalPage />} />
          </Route>

          {/* Catch-all */}
          <Route element={<Layout />}>
            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Routes>
        </Suspense>
        </A11yAuditProvider>
      <CookieConsent />
      <Suspense fallback={null}>
        <ConsultationBanner />
        <ExitIntent />
      </Suspense>
      </BrowserRouter>
      </ToastProvider>
    </QueryClientProvider>
    </Sentry.ErrorBoundary>
  );
}

export default App;
